import { workspaceFetch } from '../../../../shared/platform/business-workspaces';
/**
 * The live loopback adapter for the SYN scenario-knowledge plane.
 *
 * THE ORIGIN RULE IS THE EXISTING ONE, NOT A NEW ONE. It reuses the same guard the
 * MissionTemplate live read uses: an explicit configured transport origin, or a
 * loopback host (`localhost`, `[::1]`, or a dotted-quad 127.x.x.x). A page that
 * points this adapter at a public host fails at construction rather than at request
 * time, so a misconfigured build cannot quietly read a remote plane.
 *
 * WHAT IT REFUSES. A non-200 status is a transport failure, not a read outcome: the
 * Java plane answers 200 for every read state it has, so a 500 means the transport
 * failed and must not be rendered as "the source said nothing". A non-JSON content
 * type, a redirect, or a body that does not parse as the published document are the
 * same kind of failure. There is no fallback to a bundled fixture — the whole point
 * of this surface is that its data came from the host.
 */

import { configuredTransportOrigin } from '../../../../shared/config/api-origin';
import { KnowledgeWireError, parseRegistry, parseRequirements } from '../consumer/contract';
import type { ScenarioKnowledgeReadPort } from './scenario-knowledge-port';

const ROUTE_ROOT = '/read-models/scenario-knowledge';

function requireLoopbackOrConfigured(baseOrigin: string): URL {
  const configured = configuredTransportOrigin(baseOrigin);
  if (!configured && !/^https?:\/\/[^/?#\\\s@]+\/?$/i.test(baseOrigin)) {
    throw new Error('Root loopback origin required');
  }
  const origin = new URL(configured ?? baseOrigin);
  const parts = origin.hostname.split('.');
  if (
    !configured &&
    !(
      origin.hostname === 'localhost' ||
      origin.hostname === '[::1]' ||
      (parts.length === 4 &&
        parts[0] === '127' &&
        parts.every((part) => /^\d+$/.test(part) && Number(part) <= 255))
    )
  ) {
    throw new Error('Loopback origin required');
  }
  return origin;
}

export function createLiveScenarioKnowledgeAdapter({
  baseOrigin,
  fetchImpl,
}: {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
}): ScenarioKnowledgeReadPort {
  const origin = requireLoopbackOrConfigured(baseOrigin);
  async function read(route: string): Promise<unknown> {
    const response = await (fetchImpl ?? workspaceFetch)(origin.origin + ROUTE_ROOT + route, {
      method: 'GET',
      credentials: 'omit',
      redirect: 'error',
      cache: 'no-store',
    });
    if (response.status !== 200) {
      throw new KnowledgeWireError('scenario-knowledge HTTP failure: ' + response.status);
    }
    if (
      response.headers.get('Content-Type')?.split(';')[0]?.trim().toLowerCase() !==
      'application/json'
    ) {
      throw new KnowledgeWireError('scenario-knowledge requires a JSON response');
    }
    return response.json();
  }
  return {
    async registry() {
      return parseRegistry(await read('/registry'));
    },
    async requirements() {
      return parseRequirements(await read('/requirements'));
    },
  };
}
